import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import { numberSetService, type CustomerNumberSet } from '../../services/numberSetService'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'

/** หน้า เลขชุด — ดึงรายการเลขชุดทั้งหมดมาแสดง (ตรงกับ user-classic NumberSetPage, UI แบบ user-modern) */
const ModernLobbyHuaySetPage: React.FC = () => {
  useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()
  const [numberSets, setNumberSets] = useState<CustomerNumberSet[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadList = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const list = await numberSetService.list()
      setNumberSets(list)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'โหลดรายการไม่สำเร็จ'
      const friendly =
        msg === 'Not Found' || msg.toLowerCase().includes('not found')
          ? 'ยังไม่มีเลขชุด หรือไม่สามารถโหลดรายการได้ในขณะนี้ กรุณากด "จับวินเลข" เพื่อสร้างเลขชุด'
          : msg
      setError(friendly)
      setNumberSets([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadList()
  }, [loadList])

  const handleEditNumberSet = (id: string) => {
    navigate(`/lobby-huay/set/edit/${id}`)
  }

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('th-TH', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    } catch {
      return dateStr
    }
  }

  return (
    <>
      <div className="c_quinary text-center">
        <h3 className="c_tertiary font_md">จัดการเลขชุด</h3>
      </div>
      <div className="row mx__1 mt-3" style={{ rowGap: '0.5rem' }}>
        <div className="col-12 col-md-6 px-1">
          <Link
            to="/lobby-huay/set/create"
            className="link_pmr ins_sd box_row justify-content-center font_rg bg_gd_tertiary border_10 text-decoration-none w-100"
          >
            <div className="d-flex align-items-center ft_sd c_tertiary font_md">
              <i className="fas fa-plus-circle me-2" />
              <span>สร้างเลขชุด</span>
            </div>
          </Link>
        </div>
        <div className="col-12 col-md-6 px-1">
          <Link
            to="/lobby-huay/win-number"
            className="link_pmr ins_sd box_row justify-content-center font_rg bg_gd_quaternary border_10 text-decoration-none w-100"
          >
            <div className="d-flex align-items-center ft_sd c_tertiary font_md">
              <span>จับวินเลข</span>
            </div>
          </Link>
        </div>
      </div>
      <div className="row mx__1 mt-2" style={{ rowGap: '0.5rem' }}>
        {error && (
          <div className="col-12 px-1">
            <div className="bg_secondary border_5 bdc_primary p-2">
              <p className="c_primary mb-0 small">{error}</p>
            </div>
          </div>
        )}
        {loading ? (
          <div className="col-12 px-1 text-center py-4">
            <i className="fas fa-spinner fa-spin c_tertiary" style={{ fontSize: '1.5rem' }} />
            <p className="c_tertiary mt-2 mb-0">กำลังโหลด...</p>
          </div>
        ) : numberSets.length === 0 ? (
          <div className="col-12 px-1 text-center py-4">
            <p className="c_secondary mb-0" style={{ fontSize: '1rem' }}>
              ยังไม่มีเลขชุด กรุณากดปุ่ม &quot;จับวินเลข&quot; หรือ &quot;สร้างเลขชุด&quot; ด้านบนเพื่อเพิ่มเลขชุดใหม่
            </p>
          </div>
        ) : (
          numberSets.map((numberSet) => (
            <div key={numberSet._id} className="col-12 px-1">
              <button
                type="button"
                className="bg_secondary box_row hh_hover justify-content-between border_5 p-2 p-md-2 w-100 border-0 text-start"
                onClick={() => handleEditNumberSet(numberSet._id)}
              >
                <h5 className="font_rg c_secondary mb-0">{numberSet.name}</h5>
                <div className="bg_gd_primary c_tertiary border_5 p-1">
                  {formatDate(numberSet.updatedAt || numberSet.createdAt)}
                </div>
              </button>
            </div>
          ))
        )}
      </div>
    </>
  )
}

export default ModernLobbyHuaySetPage
